import apiClient from "utils/apiClient";
import { OVIZIA_SERVICE_BASE_URL } from "constants";

const BASE_URL = OVIZIA_SERVICE_BASE_URL;

const DEFAULT_TLDS = [
  "com",
  "net",
  "org",
  "io",
  "co",
  "app",
  "dev",
  "site",
  "online",
  "store",
  "shop",
  "tech",
];

export function toSearchName(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9.-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export async function searchDomains(domain, tlds = DEFAULT_TLDS) {
  const { data } = await apiClient.post(
    "/api/v1/domains/search",
    { domain, tlds },
    { baseURL: BASE_URL },
  );
  return data;
}

export async function checkoutDomain(
  workspaceId,
  websiteId,
  domain,
  { periodYears = 1, privacy = true, autoRenew = true, marketId = "en-US" },
) {
  const { data } = await apiClient.post(
    "/api/v1/domains/checkout",
    { workspaceId, websiteId, domain, periodYears, privacy, autoRenew, marketId },
    { baseURL: BASE_URL },
  );
  return data;
}

export async function purchaseDomain(orderId, registrant, acceptedAgreementKeys) {
  const { data } = await apiClient.post(
    `/api/v1/domains/orders/${orderId}/purchase`,
    { idempotencyKey: orderId, registrant, acceptedAgreementKeys },
    { baseURL: BASE_URL },
  );
  return data;
}

const DOMAIN_ORDER_SUCCESS_STATUSES = new Set(["REGISTERED"]);
const DOMAIN_ORDER_FAILURE_STATUSES = new Set([
  "PAYMENT_FAILED",
  "PURCHASE_FAILED",
  "REFUND_REQUIRED",
  "CANCELLED",
]);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function isDomainOrderTerminal(order) {
  const status = String(order?.status || "").toUpperCase();
  return (
    DOMAIN_ORDER_SUCCESS_STATUSES.has(status) ||
    DOMAIN_ORDER_FAILURE_STATUSES.has(status)
  );
}

export function isDomainOrderFailed(order) {
  return DOMAIN_ORDER_FAILURE_STATUSES.has(
    String(order?.status || "").toUpperCase(),
  );
}

export async function waitForDomainOrderStatus(
  orderId,
  workspaceId,
  { timeoutMs = 45000, intervalMs = 2000 } = {},
) {
  const startedAt = Date.now();
  let latestOrder = null;

  while (Date.now() - startedAt <= timeoutMs) {
    const orders = await fetchDomainOrders(workspaceId);
    latestOrder = Array.isArray(orders)
      ? orders.find(
          (order) => String(order?.id || order?.orderId) === String(orderId),
        )
      : null;

    if (isDomainOrderTerminal(latestOrder)) {
      return latestOrder;
    }

    await sleep(intervalMs);
  }

  return latestOrder;
}

function extractEmbeddedPaymentMessage(raw) {
  if (!raw) return "";
  const match = String(raw).match(/"message"\s*:\s*"([^"]+)"/i);
  return match?.[1] || String(raw);
}

export function domainOrderFailureMessage(
  order,
  fallback = "Purchase failed. Please try again.",
) {
  const code = String(order?.errorCode || "").toUpperCase();
  const raw = extractEmbeddedPaymentMessage(
    order?.errorMessage || order?.message || fallback,
  );
  const normalized = raw.toLowerCase();

  if (normalized.includes("insufficient")) {
    return "Payment failed because the card has insufficient funds. Please use another card or contact your bank.";
  }

  if (normalized.includes("declined") || normalized.includes("card_declined")) {
    return "Payment failed because the card was declined. Please use another card or contact your bank.";
  }

  if (code.includes("PAYMENT_AUTHORIZATION")) {
    return raw || "Payment authorization failed. Please use another card or try again.";
  }

  if (code === "PROVIDER_PURCHASE_FAILED") {
    return "Domain registration failed before your card was charged. Please try again.";
  }

  return raw || fallback;
}

export async function fetchOwnedDomains(workspaceId) {
  const { data } = await apiClient.get("/api/v1/domains/owned", {
    baseURL: BASE_URL,
    params: workspaceId ? { workspaceId } : {},
  });
  return data;
}


export async function fetchWebsiteDomains(websiteId) {
  const { data } = await apiClient.get(`/api/v1/domains/websites/${websiteId}/domains`, {
    baseURL: BASE_URL,
  });
  return data;
}

export async function fetchDomainOrders(workspaceId) {
  const { data } = await apiClient.get("/api/v1/domains/orders", {
    baseURL: BASE_URL,
    params: workspaceId ? { workspaceId } : {},
  });
  return data;
}

export async function fetchWorkspaceDomainRegistrations(workspaceId) {
  const { data } = await apiClient.get(
    `/api/v1/domains/workspaces/${workspaceId}/registrations`,
    { baseURL: BASE_URL },
  );
  return data;
}

// bonus
export async function connectDomain(workspaceId, websiteId, domain) {
  const { data } = await apiClient.post(
    "/api/v1/domains/connect",
    { workspaceId, websiteId, domain },
    { baseURL: BASE_URL },
  );
  return data;
}

export async function fetchDomainMappings(workspaceId) {
  const { data } = await apiClient.get("/api/v1/domains/mappings", {
    baseURL: BASE_URL,
    params: workspaceId ? { workspaceId } : {},
  });
  return data;
}

export async function fetchDomainMapping(mappingId) {
  const { data } = await apiClient.get(`/api/v1/domains/mappings/${mappingId}`, {
    baseURL: BASE_URL,
  });
  return data;
}

export async function deleteDomainMapping(mappingId) {
  const { data } = await apiClient.delete(`/api/v1/domains/mappings/${mappingId}`, {
    baseURL: BASE_URL,
  });
  return data;
}

export { DEFAULT_TLDS };
